import React, { useState, useEffect } from 'react';
import {
  Plus,
  Edit,
  Trash2,
  Check,
  X,
  DollarSign,
  Package,
  Users,
  MessageSquare,
  MapPin,
  AlertCircle,
  Database
} from 'lucide-react';
import adminPlansService from '../services/adminPlansService';

const PlansManagement = () => {
  const [activeTab, setActiveTab] = useState('cmms');
  const [cmmsPlans, setCmmsPlans] = useState([]);
  const [chatbotPlans, setChatbotPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [modalMode, setModalMode] = useState('create');
  const [editingPlan, setEditingPlan] = useState(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deletingPlan, setDeletingPlan] = useState(null);
  const [deleteError, setDeleteError] = useState(null);
  const [formData, setFormData] = useState({});
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    loadPlans();
  }, []);

  const loadPlans = async () => {
    setLoading(true);
    setError(null);
    try {
      const [cmms, chatbot] = await Promise.all([
        adminPlansService.getAllCmmsPlans(),
        adminPlansService.getAllChatbotPlans()
      ]);
      setCmmsPlans(cmms || []);
      setChatbotPlans(chatbot || []);
    } catch (err) {
      setError(err.message || 'Failed to load plans');
    } finally {
      setLoading(false);
    }
  };

  const openCreateModal = (planType) => {
    setModalMode('create');
    setEditingPlan(null);
    if (planType === 'cmms') {
      setFormData({
        planCode: '',
        planName: '',
        description: '',
        monthlyPrice: '',
        annualPrice: '',
        includedUsers: '',
        includedAssets: '',
        includedLocations: '',
        includedBranches: '',
        pricePerAdditionalBranch: '',
        overagePricePerUser: '',
        overagePricePerAsset: '',
        overagePricePerLocation: '',
        overagePricePerBranch: '',
        isActive: true,
        isTrial: false,
        trialDays: ''
      });
    } else {
      setFormData({
        planCode: '',
        planName: '',
        description: '',
        monthlyPrice: '',
        annualPrice: '',
        messagesPerMonth: '',
        maxDocumentsPerChatbot: -1,
        includedAgents: '',
        overageEnabled: false,
        overagePricePerMessage: '',
        overagePricePerMessageBlock: '',
        messagesPerOverageBlock: '',
        isActive: true,
        isTrial: false,
        trialDays: ''
      });
    }
    setShowModal(true);
  };

  const openEditModal = (plan, planType) => {
    setModalMode('edit');
    setEditingPlan(plan);
    if (planType === 'cmms') {
      setFormData({
        planCode: plan.planCode || '',
        planName: plan.planName || plan.name || '',
        description: plan.description || '',
        monthlyPrice: plan.monthlyPrice ?? '',
        annualPrice: plan.annualPrice ?? '',
        includedUsers: plan.includedUsers ?? '',
        includedAssets: plan.includedAssets ?? '',
        includedLocations: plan.includedLocations ?? '',
        includedBranches: plan.includedBranches ?? '',
        pricePerAdditionalBranch: plan.pricePerAdditionalBranch ?? '',
        overagePricePerUser: plan.overagePricePerUser ?? '',
        overagePricePerAsset: plan.overagePricePerAsset ?? '',
        overagePricePerLocation: plan.overagePricePerLocation ?? '',
        overagePricePerBranch: plan.overagePricePerBranch ?? '',
        isActive: plan.isActive ?? false,
        isTrial: plan.isTrial ?? false,
        trialDays: plan.trialDays ?? ''
      });
    } else {
      setFormData({
        planCode: plan.planCode || '',
        planName: plan.planName || plan.name || '',
        description: plan.description || '',
        monthlyPrice: plan.monthlyPrice ?? '',
        annualPrice: plan.annualPrice ?? '',
        messagesPerMonth: plan.messagesPerMonth ?? '',
        maxDocumentsPerChatbot: plan.maxDocumentsPerChatbot ?? -1,
        includedAgents: plan.includedAgents ?? '',
        overageEnabled: plan.overageEnabled ?? false,
        overagePricePerMessage: plan.overagePricePerMessage ?? '',
        overagePricePerMessageBlock: plan.overagePricePerMessageBlock ?? '',
        messagesPerOverageBlock: plan.messagesPerOverageBlock ?? '',
        isActive: plan.isActive ?? false,
        isTrial: plan.isTrial ?? false,
        trialDays: plan.trialDays ?? ''
      });
    }
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setEditingPlan(null);
    setFormData({});
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    setSubmitting(true);

    try {
      const payload = { ...formData };

      // Convert empty strings to null for numeric fields
      Object.keys(payload).forEach(key => {
        if (payload[key] === '' && key !== 'planCode' && key !== 'planName' && key !== 'description') {
          payload[key] = null;
        }
      });

      // Coerce all numeric fields from string → number (inputs always store strings)
      const intFields = [
        'maxDocumentsPerChatbot', 'includedAgents', 'messagesPerMonth',
        'messagesPerOverageBlock', 'trialDays',
        'includedUsers', 'includedAssets', 'includedLocations', 'includedBranches',
      ];
      const floatFields = [
        'monthlyPrice', 'annualPrice',
        'overagePricePerMessage', 'overagePricePerMessageBlock', 'overagePricePerUser',
        'overagePricePerAsset', 'overagePricePerLocation', 'overagePricePerBranch',
        'pricePerAdditionalBranch',
      ];
      intFields.forEach(key => {
        if (payload[key] !== null && payload[key] !== undefined && payload[key] !== '') {
          payload[key] = parseInt(payload[key], 10);
        }
      });
      floatFields.forEach(key => {
        if (payload[key] !== null && payload[key] !== undefined && payload[key] !== '') {
          payload[key] = parseFloat(payload[key]);
        }
      });

      if (activeTab === 'cmms') {
        if (modalMode === 'create') {
          await adminPlansService.createCmmsPlan(payload);
        } else {
          await adminPlansService.updateCmmsPlan(editingPlan.id, payload);
        }
      } else {
        if (modalMode === 'create') {
          await adminPlansService.createChatbotPlan(payload);
        } else {
          await adminPlansService.updateChatbotPlan(editingPlan.id, payload);
        }
      }

      await loadPlans();
      closeModal();
    } catch (err) {
      setError(err.message || 'Failed to save plan');
    } finally {
      setSubmitting(false);
    }
  };

  const openDeleteConfirm = (plan) => {
    setDeletingPlan({ ...plan, _planType: activeTab });
    setDeleteError(null);
    setShowDeleteConfirm(true);
  };

  const closeDeleteConfirm = () => {
    setShowDeleteConfirm(false);
    setDeletingPlan(null);
    setDeleteError(null);
  };

  const handleDelete = async () => {
    if (!deletingPlan) return;

    setDeleteError(null);
    setSubmitting(true);
    try {
      if (deletingPlan._planType === 'cmms') {
        await adminPlansService.deleteCmmsPlan(deletingPlan.id);
      } else {
        await adminPlansService.deleteChatbotPlan(deletingPlan.id);
      }
      await loadPlans();
      closeDeleteConfirm();
    } catch (err) {
      setDeleteError(err.message || 'Failed to delete plan');
    } finally {
      setSubmitting(false);
    }
  };

  const currentPlans = activeTab === 'cmms' ? cmmsPlans : chatbotPlans;

  if (loading) {
    return (
      <div className="flex justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Plans & Pricing Management</h1>
          <p className="text-gray-600 mt-1">Configure subscription plans for CMMS and Chatbot services</p>
        </div>
        <button
          onClick={() => openCreateModal(activeTab)}
          className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors flex items-center gap-2"
        >
          <Plus className="w-4 h-4" />
          Create {activeTab === 'cmms' ? 'CMMS' : 'Chatbot'} Plan
        </button>
      </div>

      {/* Error Message */}
      {error && (
        <div className="bg-red-50 text-red-600 p-4 rounded-lg flex items-start gap-2">
          <AlertCircle className="w-5 h-5 flex-shrink-0 mt-0.5" />
          <span>{error}</span>
        </div>
      )}

      {/* Tabs */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
        <div className="border-b border-gray-200">
          <div className="flex">
            <button
              onClick={() => setActiveTab('cmms')}
              className={`flex-1 px-6 py-4 text-sm font-medium transition-colors ${
                activeTab === 'cmms'
                  ? 'text-primary-600 border-b-2 border-primary-600 bg-primary-50'
                  : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50'
              }`}
            >
              <div className="flex items-center justify-center gap-2">
                <Package className="w-4 h-4" />
                CMMS Plans ({cmmsPlans.length})
              </div>
            </button>
            <button
              onClick={() => setActiveTab('chatbot')}
              className={`flex-1 px-6 py-4 text-sm font-medium transition-colors ${
                activeTab === 'chatbot'
                  ? 'text-primary-600 border-b-2 border-primary-600 bg-primary-50'
                  : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50'
              }`}
            >
              <div className="flex items-center justify-center gap-2">
                <MessageSquare className="w-4 h-4" />
                Chatbot Plans ({chatbotPlans.length})
              </div>
            </button>
          </div>
        </div>

        {/* Plans Grid */}
        <div className="p-6">
          {currentPlans.length === 0 ? (
            <div className="text-center py-12">
              {activeTab === 'chatbot' ? (
                <MessageSquare className="w-12 h-12 text-gray-400 mx-auto mb-3" />
              ) : (
                <Package className="w-12 h-12 text-gray-400 mx-auto mb-3" />
              )}
              <p className="text-gray-600">No {activeTab === 'cmms' ? 'CMMS' : 'Chatbot'} plans created yet</p>
              <button
                onClick={() => openCreateModal(activeTab)}
                className="mt-4 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors inline-flex items-center gap-2"
              >
                <Plus className="w-4 h-4" />
                Create First Plan
              </button>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {currentPlans.map((plan) => (
                <PlanCard
                  key={plan.id}
                  plan={plan}
                  planType={activeTab}
                  onEdit={() => openEditModal(plan, activeTab)}
                  onDelete={() => openDeleteConfirm(plan)}
                />
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Create/Edit Modal */}
      {showModal && (
        <Modal
          title={`${modalMode === 'create' ? 'Create' : 'Edit'} ${activeTab === 'cmms' ? 'CMMS' : 'Chatbot'} Plan`}
          onClose={closeModal}
        >
          <form onSubmit={handleSubmit} className="space-y-4">
            {/* Basic Info */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Plan Code <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  name="planCode"
                  value={formData.planCode || ''}
                  onChange={handleInputChange}
                  required
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent font-mono"
                  placeholder="e.g., CHATBOT_UNLIMITED"
                />
                <p className="text-xs text-gray-500 mt-1">Unique identifier, no spaces</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Plan Name <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  name="planName"
                  value={formData.planName || ''}
                  onChange={handleInputChange}
                  required
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  placeholder="e.g., Unlimited"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Description
              </label>
              <textarea
                name="description"
                value={formData.description || ''}
                onChange={handleInputChange}
                rows={2}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                placeholder="Brief description of this plan"
              />
            </div>

            {/* Pricing */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Monthly Price ($)
                </label>
                <input
                  type="number"
                  step="0.01"
                  name="monthlyPrice"
                  value={formData.monthlyPrice || ''}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  placeholder="29.99"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Annual Price ($)
                </label>
                <input
                  type="number"
                  step="0.01"
                  name="annualPrice"
                  value={formData.annualPrice || ''}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  placeholder="299.99"
                />
              </div>
            </div>

            {/* CMMS-specific fields */}
            {activeTab === 'cmms' && (
              <>
                <div className="border-t border-gray-200 pt-4">
                  <h3 className="text-sm font-medium text-gray-900 mb-3">CMMS Limits</h3>
                  <div className="grid grid-cols-3 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Max Users
                      </label>
                      <input
                        type="number"
                        name="includedUsers"
                        value={formData.includedUsers || ''}
                        onChange={handleInputChange}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                        placeholder="10"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Max Assets
                      </label>
                      <input
                        type="number"
                        name="includedAssets"
                        value={formData.includedAssets || ''}
                        onChange={handleInputChange}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                        placeholder="100"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Max Locations
                      </label>
                      <input
                        type="number"
                        name="includedLocations"
                        value={formData.includedLocations || ''}
                        onChange={handleInputChange}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                        placeholder="5"
                      />
                    </div>
                  </div>

                  {/* Branches */}
                  <div className="grid grid-cols-2 gap-4 mt-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Included Branches
                      </label>
                      <input
                        type="number"
                        min="0"
                        name="includedBranches"
                        value={formData.includedBranches ?? ''}
                        onChange={handleInputChange}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                        placeholder="Leave blank for unlimited"
                      />
                      <p className="text-xs text-gray-500 mt-1">Blank = unlimited</p>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Price per Additional Branch ($)
                      </label>
                      <input
                        type="number"
                        min="0"
                        step="0.01"
                        name="pricePerAdditionalBranch"
                        value={formData.pricePerAdditionalBranch ?? ''}
                        onChange={handleInputChange}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                        placeholder="0.00"
                      />
                    </div>
                  </div>
                </div>

                <div className="border-t border-gray-200 pt-4">
                  <h3 className="text-sm font-medium text-gray-900 mb-3">Overage Pricing</h3>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Per User ($)
                      </label>
                      <input
                        type="number"
                        step="0.01"
                        name="overagePricePerUser"
                        value={formData.overagePricePerUser || ''}
                        onChange={handleInputChange}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                        placeholder="5.00"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Per Asset ($)
                      </label>
                      <input
                        type="number"
                        step="0.01"
                        name="overagePricePerAsset"
                        value={formData.overagePricePerAsset || ''}
                        onChange={handleInputChange}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                        placeholder="0.50"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Per Location ($)
                      </label>
                      <input
                        type="number"
                        step="0.01"
                        name="overagePricePerLocation"
                        value={formData.overagePricePerLocation || ''}
                        onChange={handleInputChange}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                        placeholder="10.00"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Per Branch ($)
                      </label>
                      <input
                        type="number"
                        step="0.01"
                        name="overagePricePerBranch"
                        value={formData.overagePricePerBranch || ''}
                        onChange={handleInputChange}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                        placeholder="0.00"
                      />
                    </div>
                  </div>
                </div>
              </>
            )}

            {/* Chatbot-specific fields */}
            {activeTab === 'chatbot' && (
              <>
                <div className="border-t border-gray-200 pt-4">
                  <h3 className="text-sm font-medium text-gray-900 mb-3">Chatbot Limits</h3>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Messages Per Month
                    </label>
                    <input
                      type="number"
                      name="messagesPerMonth"
                      value={formData.messagesPerMonth || ''}
                      onChange={handleInputChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                      placeholder="1000"
                    />
                  </div>

                  <div className="mt-4">
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Max Documents per Chatbot
                    </label>
                    <input
                      type="number"
                      name="maxDocumentsPerChatbot"
                      value={formData.maxDocumentsPerChatbot ?? ''}
                      onChange={handleInputChange}
                      min="-1"
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                      placeholder="-1 for unlimited"
                    />
                    <p className="text-xs text-gray-500 mt-1">-1 = unlimited. Enter a positive number to cap uploads per chatbot.</p>
                  </div>

                  <div className="mt-4">
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Included Agents
                    </label>
                    <input
                      type="number"
                      min="0"
                      name="includedAgents"
                      value={formData.includedAgents ?? ''}
                      onChange={handleInputChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                      placeholder="Leave blank for unlimited"
                    />
                    <p className="text-xs text-gray-500 mt-1">Blank = unlimited. Number of live-chat agents allowed on this plan.</p>
                  </div>

                  <div className="flex items-center gap-3 mt-4">
                    <label className="text-sm font-medium text-gray-700">Enable Overage Billing</label>
                    <button
                      type="button"
                      onClick={() => setFormData(prev => ({ ...prev, overageEnabled: !prev.overageEnabled }))}
                      className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none ${
                        formData.overageEnabled ? 'bg-blue-600' : 'bg-gray-200'
                      }`}
                    >
                      <span
                        className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                          formData.overageEnabled ? 'translate-x-6' : 'translate-x-1'
                        }`}
                      />
                    </button>
                  </div>

                  {formData.overageEnabled && (
                    <div className="mt-4">
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Overage Price per Message ($)
                      </label>
                      <input
                        type="number"
                        min="0"
                        step="0.0001"
                        name="overagePricePerMessage"
                        value={formData.overagePricePerMessage ?? ''}
                        onChange={handleInputChange}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                        placeholder="0.0010"
                      />
                    </div>
                  )}
                </div>

                <div className="border-t border-gray-200 pt-4">
                  <h3 className="text-sm font-medium text-gray-900 mb-3">Block-based Overage Pricing</h3>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Price Per Block ($)
                      </label>
                      <input
                        type="number"
                        step="0.01"
                        name="overagePricePerMessageBlock"
                        value={formData.overagePricePerMessageBlock || ''}
                        onChange={handleInputChange}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                        placeholder="10.00"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Messages Per Block
                      </label>
                      <input
                        type="number"
                        name="messagesPerOverageBlock"
                        value={formData.messagesPerOverageBlock || ''}
                        onChange={handleInputChange}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                        placeholder="1000"
                      />
                    </div>
                  </div>
                </div>
              </>
            )}

            {/* Stripe Product ID (Edit Mode Only) */}
            {editingPlan && editingPlan.stripeProductId && (
              <div className="border-t border-gray-200 pt-4">
                <label className="block text-sm font-medium text-gray-700 mb-1">Stripe Product ID</label>
                <input
                  type="text"
                  value={editingPlan.stripeProductId}
                  disabled
                  className="w-full border border-gray-200 rounded-lg px-3 py-2 bg-gray-50 text-gray-500 text-sm"
                />
              </div>
            )}

            {/* Trial Plan Configuration */}
            <div className="border-t border-gray-200 pt-4">
              <label className="flex items-center gap-2 cursor-pointer mb-3">
                <input
                  type="checkbox"
                  name="isTrial"
                  checked={formData.isTrial === true}
                  onChange={handleInputChange}
                  className="w-4 h-4 text-amber-600 border-gray-300 rounded focus:ring-amber-500"
                />
                <span className="text-sm font-medium text-gray-700">Mark as Trial Plan</span>
              </label>

              {formData.isTrial && (
                <>
                  <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 mb-3 text-sm text-amber-800">
                    Saving will deactivate the current active trial plan for this service type.
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Trial Duration (days) <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="number"
                      min="1"
                      name="trialDays"
                      value={formData.trialDays || ''}
                      onChange={handleInputChange}
                      required={formData.isTrial === true}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-transparent"
                      placeholder="14"
                    />
                  </div>
                </>
              )}
            </div>

            {/* Status Toggle */}
            <div className="border-t border-gray-200 pt-4">
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  name="isActive"
                  checked={formData.isActive === true}
                  onChange={handleInputChange}
                  className="w-4 h-4 text-primary-600 border-gray-300 rounded focus:ring-primary-500"
                />
                <span className="text-sm font-medium text-gray-700">Active (available for subscription)</span>
              </label>
            </div>

            {/* Form Actions */}
            <div className="flex gap-3 pt-4 border-t border-gray-200">
              <button
                type="button"
                onClick={closeModal}
                disabled={submitting}
                className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={submitting}
                className="flex-1 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {submitting ? 'Saving...' : (modalMode === 'create' ? 'Create Plan' : 'Update Plan')}
              </button>
            </div>
          </form>
        </Modal>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteConfirm && deletingPlan && (
        <Modal title="Confirm Delete" onClose={closeDeleteConfirm}>
          <div className="space-y-4">
            <p className="text-gray-700">
              Are you sure you want to delete the plan <strong>{deletingPlan.planName || deletingPlan.name}</strong>?
            </p>
            <p className="text-sm text-gray-600">
              This will deactivate the plan. If there are active subscriptions, the deletion will fail.
            </p>

            {deleteError && (
              <div className="bg-red-50 text-red-600 p-3 rounded-lg flex items-start gap-2 text-sm">
                <AlertCircle className="w-5 h-5 flex-shrink-0 mt-0.5" />
                <span>{deleteError}</span>
              </div>
            )}

            <div className="flex gap-3 pt-2">
              <button
                type="button"
                onClick={closeDeleteConfirm}
                disabled={submitting}
                className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleDelete}
                disabled={submitting}
                className="flex-1 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {submitting ? 'Deleting...' : 'Delete Plan'}
              </button>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
};

// Plan Card Component
const PlanCard = ({ plan, planType, onEdit, onDelete }) => {
  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden hover:shadow-md transition-shadow">
      {/* Header */}
      <div className="bg-gradient-to-r from-primary-600 to-blue-600 text-white p-6">
        <div className="flex items-center gap-2 mb-2">
          <h3 className="text-xl font-bold">{plan.planName || plan.name}</h3>
          {plan.isTrial && (
            <span className="px-2 py-0.5 bg-amber-400 text-amber-900 text-xs font-bold rounded-full">
              TRIAL
            </span>
          )}
        </div>
        <p className="text-sm text-white/80 line-clamp-2">{plan.description || 'No description'}</p>
        {plan.isTrial && plan.trialDays && (
          <p className="text-xs text-amber-200 mt-1">{plan.trialDays}-day trial</p>
        )}
      </div>

      {/* Pricing */}
      <div className="p-6 border-b border-gray-200">
        <div className="space-y-2">
          {plan.monthlyPrice !== null && (
            <div className="flex items-center justify-between">
              <span className="text-sm text-gray-600">Monthly:</span>
              <span className="text-lg font-bold text-gray-900">${Number(plan.monthlyPrice).toFixed(2)}</span>
            </div>
          )}
          {plan.annualPrice !== null && (
            <div className="flex items-center justify-between">
              <span className="text-sm text-gray-600">Annual:</span>
              <span className="text-lg font-bold text-gray-900">${Number(plan.annualPrice).toFixed(2)}</span>
            </div>
          )}
          {plan.monthlyPrice === null && plan.annualPrice === null && (
            <div className="text-sm text-gray-500 italic">No pricing set</div>
          )}
        </div>
      </div>

      {/* Plan Details */}
      <div className="p-6 space-y-3">
        {planType === 'cmms' ? (
          <>
            <div className="flex items-center justify-between text-sm">
              <span className="text-gray-600 flex items-center gap-1">
                <Users className="w-4 h-4" />
                Max Users
              </span>
              <span className="font-semibold text-gray-900">{plan.includedUsers != null ? plan.includedUsers : 'Unlimited'}</span>
            </div>
            <div className="flex items-center justify-between text-sm">
              <span className="text-gray-600 flex items-center gap-1">
                <Package className="w-4 h-4" />
                Max Assets
              </span>
              <span className="font-semibold text-gray-900">{plan.includedAssets != null ? plan.includedAssets : 'Unlimited'}</span>
            </div>
            <div className="flex items-center justify-between text-sm">
              <span className="text-gray-600 flex items-center gap-1">
                <MapPin className="w-4 h-4" />
                Max Locations
              </span>
              <span className="font-semibold text-gray-900">{plan.includedLocations != null ? plan.includedLocations : 'Unlimited'}</span>
            </div>
            {plan.includedBranches != null && (
              <div className="flex items-center justify-between text-sm">
                <span className="text-gray-600 flex items-center gap-1">
                  <MapPin className="w-4 h-4" />
                  Max Branches
                </span>
                <span className="font-semibold text-gray-900">{plan.includedBranches}</span>
              </div>
            )}

            {(plan.overagePricePerUser != null || plan.overagePricePerAsset != null || plan.overagePricePerLocation != null || plan.overagePricePerBranch != null) && (
              <div className="border-t border-gray-200 pt-3 mt-3">
                <div className="text-xs font-medium text-gray-500 mb-2">Overage Pricing</div>
                {plan.overagePricePerUser != null && (
                  <div className="flex items-center justify-between text-xs">
                    <span className="text-gray-600">Per User:</span>
                    <span className="font-medium text-gray-900">${Number(plan.overagePricePerUser).toFixed(2)}</span>
                  </div>
                )}
                {plan.overagePricePerAsset != null && (
                  <div className="flex items-center justify-between text-xs">
                    <span className="text-gray-600">Per Asset:</span>
                    <span className="font-medium text-gray-900">${Number(plan.overagePricePerAsset).toFixed(2)}</span>
                  </div>
                )}
                {plan.overagePricePerLocation != null && (
                  <div className="flex items-center justify-between text-xs">
                    <span className="text-gray-600">Per Location:</span>
                    <span className="font-medium text-gray-900">${Number(plan.overagePricePerLocation).toFixed(2)}</span>
                  </div>
                )}
                {plan.overagePricePerBranch != null && (
                  <div className="flex items-center justify-between text-xs">
                    <span className="text-gray-600">Per Branch:</span>
                    <span className="font-medium text-gray-900">${Number(plan.overagePricePerBranch).toFixed(2)}</span>
                  </div>
                )}
              </div>
            )}
          </>
        ) : (
          <>
            <div className="flex items-center justify-between text-sm">
              <span className="text-gray-600 flex items-center gap-1">
                <MessageSquare className="w-4 h-4" />
                Messages/Month
              </span>
              <span className="font-semibold text-gray-900">{plan.messagesPerMonth?.toLocaleString() || 'Unlimited'}</span>
            </div>

            <div className="flex items-center justify-between text-sm">
              <span className="text-gray-600 flex items-center gap-1">
                <Database className="w-4 h-4" />
                Docs/Chatbot
              </span>
              <span className="font-semibold text-gray-900">
                {plan.maxDocumentsPerChatbot === -1 || plan.maxDocumentsPerChatbot == null ? 'Unlimited' : plan.maxDocumentsPerChatbot}
              </span>
            </div>

            <div className="flex items-center justify-between text-sm">
              <span className="text-gray-600 flex items-center gap-1">
                <Users className="w-4 h-4" />
                Agents
              </span>
              <span className="font-semibold text-gray-900">
                {plan.includedAgents != null ? plan.includedAgents : 'Unlimited'}
              </span>
            </div>

            {(plan.overagePricePerMessageBlock != null || plan.messagesPerOverageBlock != null) && (
              <div className="border-t border-gray-200 pt-3 mt-3">
                <div className="text-xs font-medium text-gray-500 mb-2">Block-based Overage</div>
                {plan.overagePricePerMessageBlock != null && (
                  <div className="flex items-center justify-between text-xs">
                    <span className="text-gray-600">Per Block:</span>
                    <span className="font-medium text-gray-900">${Number(plan.overagePricePerMessageBlock).toFixed(2)}</span>
                  </div>
                )}
                {plan.messagesPerOverageBlock != null && (
                  <div className="flex items-center justify-between text-xs">
                    <span className="text-gray-600">Messages/Block:</span>
                    <span className="font-medium text-gray-900">{plan.messagesPerOverageBlock.toLocaleString()}</span>
                  </div>
                )}
              </div>
            )}

            {plan.overageEnabled && (
              <div className="border-t border-gray-200 pt-3 mt-3">
                <div className="text-xs font-medium text-gray-500 mb-2">Per-Message Overage</div>
                <div className="flex items-center justify-between text-xs">
                  <span className="text-gray-600">Overage Enabled:</span>
                  <span className="font-medium text-green-600">Yes</span>
                </div>
                {plan.overagePricePerMessage != null && (
                  <div className="flex items-center justify-between text-xs">
                    <span className="text-gray-600">Per Message:</span>
                    <span className="font-medium text-gray-900">${Number(plan.overagePricePerMessage).toFixed(4)}</span>
                  </div>
                )}
              </div>
            )}
          </>
        )}

        {/* Stripe IDs (if present) */}
        {plan.stripeProductId && (
          <div className="border-t border-gray-200 pt-3 mt-3">
            <div className="text-xs text-gray-500">
              <div className="mb-1">Stripe Product ID:</div>
              <div className="font-mono text-gray-700 break-all">{plan.stripeProductId}</div>
            </div>
          </div>
        )}
      </div>

      {/* Actions */}
      <div className="px-6 pb-6 flex gap-2">
        <button
          onClick={onEdit}
          className="flex-1 px-4 py-2 bg-primary-50 text-primary-600 rounded-lg hover:bg-primary-100 transition-colors flex items-center justify-center gap-2"
        >
          <Edit className="w-4 h-4" />
          Edit
        </button>
        <button
          onClick={onDelete}
          className="px-4 py-2 bg-red-50 text-red-600 rounded-lg hover:bg-red-100 transition-colors"
        >
          <Trash2 className="w-4 h-4" />
        </button>
      </div>

      {/* Status Badge */}
      <div className="px-6 pb-6">
        <div className={`text-center text-sm py-2 rounded-lg ${
          plan.isActive ? 'bg-green-50 text-green-700' : 'bg-gray-50 text-gray-700'
        }`}>
          {plan.isActive ? (
            <span className="flex items-center justify-center gap-1">
              <Check className="w-4 h-4" />
              Active
            </span>
          ) : (
            <span className="flex items-center justify-center gap-1">
              <X className="w-4 h-4" />
              Inactive
            </span>
          )}
        </div>
      </div>
    </div>
  );
};

// Modal Component
const Modal = ({ title, children, onClose }) => {
  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between">
          <h2 className="text-xl font-bold text-gray-900">{title}</h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <X className="w-6 h-6" />
          </button>
        </div>
        <div className="p-6">
          {children}
        </div>
      </div>
    </div>
  );
};

export default PlansManagement;
